import { Instagram } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EXTERNAL_LINKS } from '../../config/app.config'

// Custom X (Twitter) icon
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

// Custom Telegram icon
function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  )
}

export default function Footer() {
  const { t } = useTranslation(['footer', 'navigation'])
  const currentYear = new Date().getFullYear()

  return (
    <footer className="border-t border-green-900/20 bg-black">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-6">
          {/* Brand */}
          <div className="space-y-3 text-center md:text-left">
            <div className="flex justify-center md:justify-start">
              <img
                src="/images/brand/logo-takara.png"
                alt="Takara"
                className="h-8 w-auto"
              />
            </div>
            <p className="text-sm text-gray-400">
              {t('footer:tagline')}
            </p>
          </div>

          {/* Products */}
          <div className='flex flex-col items-center md:items-start text-center md:text-left'>
            <h3 className="text-sm font-semibold text-gold-500 mb-3">{t('footer:sections.products')}</h3>
            <ul className="space-y-1">
              <li>
                <a href="/app/vaults" className="text-sm text-gray-400 hover:text-gold-400 transition-colors">
                  {t('navigation:vaults')}
                </a>
              </li>
              <li>
                <a href="/app/marketplace" className="text-sm text-gray-400 hover:text-gold-400 transition-colors">
                  {t('navigation:marketplace')}
                </a>
              </li>
              <li>
                <a href="/app/dashboard" className="text-sm text-gray-400 hover:text-gold-400 transition-colors">
                  {t('navigation:dashboard')}
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div className='flex flex-col items-center md:items-start text-center md:text-left'>
            <h3 className="text-sm font-semibold text-gold-500 mb-3">{t('footer:sections.resources')}</h3>
            <ul className="space-y-1">
              <li>
                <a
                  href={EXTERNAL_LINKS.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-400 hover:text-gold-400 transition-colors"
                >
                  {t('footer:links.docs')}
                </a>
              </li>
              <li>
                <a href="/app/faq" className="text-sm text-gray-400 hover:text-gold-400 transition-colors">
                  {t('navigation:faq')}
                </a>
              </li>
              <li>
                <a href="/app/legal" className="text-sm text-gray-400 hover:text-gold-400 transition-colors">
                  {t('footer:links.legal')}
                </a>
              </li>
            </ul>
          </div>

          {/* Community */}
          <div className='flex flex-col items-center md:items-end text-center'>
            <h3 className="text-sm font-semibold text-gold-500 mb-3">{t('footer:sections.community')}</h3>
            <div className="flex space-x-4">
              <a
                href={EXTERNAL_LINKS.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gold-400 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={EXTERNAL_LINKS.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gold-400 transition-colors"
                aria-label="X (Twitter)"
              >
                <XIcon className="h-5 w-5" />
              </a>
              <a
                href={EXTERNAL_LINKS.social.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gold-400 transition-colors"
                aria-label="Telegram"
              >
                <TelegramIcon className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-green-900/20">
          {/* Mobile: Text first, then all icons */}
          <div className="flex flex-col md:hidden items-center gap-6 w-full">
            {/* Copyright Text */}
            <div className="text-center space-y-1">
              <p className="text-sm text-gold-500">
                © Takara
              </p>
              <p className="text-xs text-gray-500">
                {t('footer:copyright.entity')}
              </p>
            </div>
            {/* All 4 Certifications */}
            <div className="flex flex-col items-center gap-4 w-full">
              {/* First row: 3 certifications with equal width */}
              <div className="flex items-center justify-center gap-4 w-full">
                <div className="w-1/3 flex items-center justify-center">
                  <img
                    src="/images/certifications/iso-27001.svg"
                    alt="ISO 27001"
                    className="h-14 w-14 object-contain opacity-80 hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="w-1/3 flex items-center justify-center">
                  <img
                    src="/images/certifications/iso-20022.svg"
                    alt="ISO 20022"
                    className="h-14 w-14 object-contain opacity-80 hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="w-1/3 flex items-center justify-center">
                  <img
                    src="/images/certifications/pci-dss.svg"
                    alt="PCI DSS"
                    className="h-14 w-14 object-contain opacity-80 hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>
              {/* Second row: 1 certification with 70% width */}
              <div className="flex items-center justify-center w-full">
                <div className="w-[70%] flex items-center justify-center">
                  <img
                    src="/images/certifications/aml-cft.svg"
                    alt="AML/CFT"
                    className="h-8 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: Left icons, center text, right icons */}
          <div className="hidden md:flex flex-row justify-between items-center w-full">
            {/* Left: ISO Certifications */}
              <img
                src="/images/certifications/iso-27001.svg"
                alt="ISO 27001"
                className="h-16 w-16 object-contain opacity-80 hover:opacity-100 transition-opacity"
              />
              <img
                src="/images/certifications/iso-20022.svg"
                alt="ISO 20022"
                className="h-16 w-16 object-contain opacity-80 hover:opacity-100 transition-opacity"
              />

            {/* Center: Copyright */}
            <div className="text-center space-y-1 flex-shrink-0">
              <p className="text-sm text-gold-500">
                © Takara
              </p>
              <p className="text-xs text-gray-500">
                {t('footer:copyright.entity')}
              </p>
            </div>

            {/* Right: Compliance Certifications */}
              <img 
                src="/images/certifications/pci-dss.svg" 
                alt="PCI DSS" 
                className="h-16 w-16 object-contain opacity-80 hover:opacity-100 transition-opacity" 
              />
              <img 
                src="/images/certifications/aml-cft.svg" 
                alt="AML/CFT" 
                className="h-10 w-auto max-w-[80px] object-contain opacity-80 hover:opacity-100 transition-opacity" 
              />
          </div>
        </div>
      </div>
    </footer>
  )
}
